import React, { useId, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useFetchWrapper } from '../../../_helpers/fetchWrapper.js';
import { parseTipAmount } from '../modal/TipModal.jsx';
import config from '../../../config.js';
import '../../../styles/TipPanel.css';
import '../../../styles/StreamCta.css';

/** The longest message to the performer, in characters (ENG-682). */
export const TIP_MESSAGE_MAX = 200;

/** Checkout errors with their own message; anything else is generic. */
const TIP_ERRORS = {
  tip_message_too_long: 'stream.tip.errors.messageTooLong',
  tip_message_refused: 'stream.tip.errors.messageRefused',
  tip_amount_below_minimum: 'stream.tip.errors.amountRange',
  tip_amount_above_maximum: 'stream.tip.errors.amountRange',
};

/**
 * "TIP THE PERFORMER · Send a tip" (mockup 08, ENG-681), in the performer
 * card's place while open. The amount chips are the band's presets from
 * `GET /public/bands/{id}` (`tipPresetsCents`, ENG-682); Custom takes a typed
 * amount under the ENG-502 rules. An optional message goes to the performer
 * only. Send hands the payer to Stripe Checkout, which comes back to this
 * page; the tip then shows in chat as the MOST RECENT highlight.
 */
const TipPanel = ({ bandId, presetsCents = [], onClose }) => {
  const { t, format, locale } = useLocale();
  const fetchWrapperRef = useRef(useFetchWrapper());
  const headingId = useId();
  const messageId = useId();
  const [choice, setChoice] = useState(presetsCents.length ? presetsCents[0] : 'custom');
  const [custom, setCustom] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const usd = (cents) => format.usd(cents / 100, { cents: 'auto' });
  const parsed = choice === 'custom' ? parseTipAmount(custom) : { amount: choice / 100, error: null };
  const amountCents = parsed.amount != null ? Math.round(parsed.amount * 100) : null;
  const messageLength = [...message].length;
  const canSend = amountCents != null && messageLength <= TIP_MESSAGE_MAX && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const answer = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/public/bands/${bandId}/tips/checkout`, {
        amount_cents: amountCents,
        message: message.trim(),
        locale,
        return_path: window.location.pathname,
      });
      if (!answer?.checkout_url) throw new Error('no checkout url');
      window.location.assign(answer.checkout_url);
    } catch (err) {
      setSending(false);
      setError(TIP_ERRORS[err?.code] ?? 'stream.tip.errors.generic');
    }
  };

  return (
    <section className="tip-panel" data-testid="tip-panel" aria-labelledby={headingId}>
      <header className="tip-panel-header">
        <div>
          <span className="tip-panel-kicker">{t('stream.tip.kicker')}</span>
          <h2 className="tip-panel-title" id={headingId}>{t('stream.tip.title')}</h2>
        </div>
        <button type="button" className="tip-panel-close" onClick={onClose} aria-label={t('stream.tip.close')} data-testid="tip-panel-close">
          <FaTimes aria-hidden="true" />
        </button>
      </header>

      <div className="tip-panel-chips" role="radiogroup" aria-label={t('stream.tip.amountLabel')}>
        {presetsCents.map((cents) => (
          <button
            key={cents}
            type="button"
            role="radio"
            aria-checked={choice === cents}
            className={`tip-panel-chip${choice === cents ? ' tip-panel-chip--on' : ''}`}
            onClick={() => setChoice(cents)}
          >
            {usd(cents)}
          </button>
        ))}
        <button
          type="button"
          role="radio"
          aria-checked={choice === 'custom'}
          className={`tip-panel-chip${choice === 'custom' ? ' tip-panel-chip--on' : ''}`}
          onClick={() => setChoice('custom')}
          data-testid="tip-panel-custom"
        >
          {t('stream.tip.custom')}
        </button>
      </div>

      {choice === 'custom' && (
        <label className="tip-panel-custom">
          <span className="tip-panel-currency" aria-hidden="true">{t('tip.currencySymbol')}</span>
          <input
            type="text"
            inputMode="decimal"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder={t('tip.customPlaceholder')}
            aria-label={t('tip.customLabel')}
            aria-invalid={Boolean(parsed.error) || undefined}
            data-testid="tip-panel-custom-amount"
          />
        </label>
      )}
      {choice === 'custom' && parsed.error && (
        <p className="tip-panel-hint" role="alert">{t(parsed.error)}</p>
      )}

      <label className="tip-panel-message" htmlFor={messageId}>
        <span className="tip-panel-sr">{t('stream.tip.messageLabel')}</span>
        <textarea
          id={messageId}
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('stream.tip.messagePlaceholder')}
          data-testid="tip-panel-message"
        />
        <span className={`tip-panel-count${messageLength > TIP_MESSAGE_MAX ? ' tip-panel-count--over' : ''}`} aria-live="polite">
          {t('stream.tip.messageCount', { count: format.number(messageLength), max: format.number(TIP_MESSAGE_MAX) })}
        </span>
      </label>

      <button type="button" className="tip-panel-send stream-cta" onClick={send} disabled={!canSend} data-testid="tip-panel-send">
        {amountCents != null ? t('stream.tip.send', { amount: usd(amountCents) }) : t('stream.tip.chooseAmount')}
      </button>
      {error && <p className="tip-panel-error" role="alert">{t(error)}</p>}
    </section>
  );
};

export default TipPanel;
